'use client'

import {useEffect, useMemo} from 'react'
import {usePathname, useSearchParams} from 'next/navigation'
import {useSession} from 'next-auth/react'
import {trackPageView, usePostHog} from '@/lib/analytics/posthog'
import {ConsentBanner} from '@/components/ConsentBanner'
import logger from '@/app/logger'
import {useUserRoles} from "@/hooks/use-user-roles";
import {useUserLimits} from "@/hooks/queries/use-users";

export function PostHogAnalyticsAnonymous() {
  return <PostHogAnalyticsInternal userProperties={{isLoggedIn: false}}/>
}

export function PostHogAnalytics() {
  const {data: session} = useSession()
  const {admin, canCreateCompany} = useUserRoles()
  const {limits} = useUserLimits()
  const userId = session?.user?.id
  const email = session?.user?.email
  const paid = limits?.paid;
  const forcedReadonly = limits?.forcedReadonly;
  const userProperties = useMemo(() => ({
    isLoggedIn: !!userId,
    admin,
    canCreateCompany,
    email,
    paid,
    forcedReadonly,
  }), [userId, admin, canCreateCompany, email, paid, forcedReadonly])

  return <PostHogAnalyticsInternal userId={userId} userProperties={userProperties}/>
}

function PostHogAnalyticsInternal({
                                    userId,
                                    userProperties
                                  }: {
  userId?: string;
  userProperties?: Record<string, unknown>;
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // Initialize PostHog
  const initialized = usePostHog(userId, userProperties)

  // Track page views when the path changes
  useEffect(() => {
    if (!initialized) {
      return
    }

    try {
      const paramsStr = searchParams.toString();
      const url = paramsStr.length > 0 ? `${pathname}?${paramsStr}` : pathname

      trackPageView(url, {
        userId: userId || 'anonymous',
        path: pathname,
        referrer: document.referrer,
        title: document.title,
      })
    } catch (error) {
      logger.error({error}, 'Failed to track page view')
    }
  }, [pathname, searchParams, userId, initialized])

  return (
    <>
      <ConsentBanner/>
    </>
  )
}